import ErrorStackParser from 'error-stack-parser';
import { fromError } from 'stacktrace-js';

const cleanFilePath = (filePath: string): string => filePath.replace(/^_N_E\//, ''); // Remove _N_E/ prefix

export interface BugsnagStackFrame {
    file?: string;
    method?: string;
    lineNumber?: number;
    columnNumber?: number;
}

export interface BugsnagException {
    errorClass: string;
    errorMessage: string;
    type: string;
    stacktrace: BugsnagStackFrame[];
    message: string;
}

export interface BugsnagEvent {
    payloadVersion: string;
    exceptions: BugsnagException[];
    severity: 'error' | 'warning' | 'info';
    unhandled: boolean;
    severityReason: {
        type: string;
    };
    app: {
        releaseStage: string;
        version: string;
        type: string;
        duration?: number;
    };
    device: {
        locale?: string;
        userAgent?: string;
        orientation?: string;
        id: string;
        time: string;
    };
    request?: {
        url?: string;
        method?: string;
        headers?: Record<string, string>;
    };
    breadcrumbs?: Array<{
        type: string;
        name: string;
        timestamp: string;
        metaData?: Record<string, unknown>;
    }>;
    context?: string;
    metaData?: Record<string, unknown>;
    user?: {
        id?: string;
        name?: string;
        email?: string;
    };
    session?: {
        id: string;
        startedAt: string;
        events: {
            handled: number;
            unhandled: number;
        };
    };
    featureFlags?: unknown[];
}

export interface BugsnagPayload {
    apiKey: string;
    notifier: {
        name: string;
        version: string;
        url: string;
    };
    events: BugsnagEvent[];
}

export const parseStackTrace = async (error: Error): Promise<BugsnagStackFrame[]> => {
    try {
        const parsedFrames = ErrorStackParser.parse(error);

        const mapped = parsedFrames.map(frame => ({
            method: frame.functionName || '<anonymous>',
            file: frame.fileName ? decodeURIComponent(frame.fileName).replace(/^.*[\\/]/, '') : undefined,
            lineNumber: frame.lineNumber,
            columnNumber: frame.columnNumber,
        }));

        return mapped;
    } catch {
        return [];
    }
};

export const getUserAgent = (): string | undefined => {
    if (typeof window !== 'undefined') {
        return window.navigator.userAgent;
    }
    return undefined;
};

export const getCurrentUrl = (): string | undefined => {
    if (typeof window !== 'undefined') {
        return window.location.href;
    }
    return undefined;
};

export const formatContext = async ({ error, path }: { error: Error; path?: string }): Promise<string> => {
    try {
        const stackFrames = await fromError(error);
        const functionName = stackFrames?.[0]?.functionName ?? 'Unknown';
        const rawFileName = stackFrames?.[0]?.fileName ? decodeURIComponent(stackFrames[0].fileName) : 'Unknown';
        const fileName = cleanFilePath(rawFileName);
        return `${functionName}@${fileName}${path ? `#${decodeURIComponent(path)}` : ''}`;
    } catch {
        return 'Unknown';
    }
};

export const reportBugsnagError = async (error: Error, context: string, metaData: object, bugsnagAPIKey?: string) => {
    try {
        const apiKey = bugsnagAPIKey || process.env.NEXT_PUBLIC_BUGSNAG_KEY;
        if (!apiKey) {
            console.warn('Bugsnag API key not configured');
            return;
        }

        const stackTrace = await parseStackTrace(error);

        const event: BugsnagEvent = {
            payloadVersion: '4',
            exceptions: [
                {
                    errorClass: error.name || 'Error',
                    errorMessage: error.message,
                    type: typeof window !== 'undefined' ? 'browserjs' : 'nodejs',
                    stacktrace: stackTrace,
                    message: error.message,
                },
            ],
            severity: 'error',
            unhandled: false,
            severityReason: {
                type: 'handledException',
            },
            app: {
                releaseStage: process.env.NEXT_PUBLIC_WRANGLER_ENV || '',
                version: process.env.NEXT_PUBLIC_BUILD_ID || '',
                type: typeof window !== 'undefined' ? 'browser' : 'server',
            },
            device: {
                locale: typeof window !== 'undefined' ? window.navigator.language : undefined,
                userAgent: getUserAgent(),
                id: crypto.randomUUID(),
                time: new Date().toISOString(),
            },
            request: {
                url: getCurrentUrl(),
            },
            breadcrumbs: [],
            context: context,
            metaData: metaData as Record<string, unknown>,
            user: {},
            session: {
                id: crypto.randomUUID(),
                startedAt: new Date().toISOString(),
                events: {
                    handled: 0,
                    unhandled: 0,
                },
            },
            featureFlags: [],
        };

        const payload: BugsnagPayload = {
            apiKey,
            notifier: {
                name: 'Custom Bugsnag Client',
                version: '1.0.0',
                url: 'https://github.com/wemod/wand',
            },
            events: [event],
        };

        const response = await fetch('https://notify.bugsnag.com', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Bugsnag-Api-Key': apiKey,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            console.error(`Failed to report error to Bugsnag: ${response.status} ${response.statusText}`);
        }
    } catch (reportError) {
        console.error('Error reporting to Bugsnag:', reportError);
    }
};
